import React, { useState } from 'react';

// Maxillary arch teeth positions arranged in a U-shape
// Each tooth: { id, label, cx, cy, width, height, angle }
const UPPER_TEETH = [
  // Right side (patient's right = viewer's left)
  { id: 'UR8', num: 1, cx: 45, cy: 155, w: 22, h: 20, angle: -45 },
  { id: 'UR7', num: 2, cx: 55, cy: 130, w: 22, h: 20, angle: -35 },
  { id: 'UR6', num: 3, cx: 62, cy: 105, w: 24, h: 22, angle: -25 },
  { id: 'UR5', num: 4, cx: 66, cy: 80, w: 18, h: 18, angle: -15 },
  { id: 'UR4', num: 5, cx: 68, cy: 60, w: 18, h: 18, angle: -10 },
  { id: 'UR3', num: 6, cx: 72, cy: 42, w: 16, h: 20, angle: -5 },
  { id: 'UR2', num: 7, cx: 82, cy: 28, w: 14, h: 18, angle: 0 },
  { id: 'UR1', num: 8, cx: 96, cy: 22, w: 18, h: 20, angle: 0 },
  // Left side (patient's left = viewer's right)
  { id: 'UL1', num: 9, cx: 114, cy: 22, w: 18, h: 20, angle: 0 },
  { id: 'UL2', num: 10, cx: 128, cy: 28, w: 14, h: 18, angle: 0 },
  { id: 'UL3', num: 11, cx: 138, cy: 42, w: 16, h: 20, angle: 5 },
  { id: 'UL4', num: 12, cx: 142, cy: 60, w: 18, h: 18, angle: 10 },
  { id: 'UL5', num: 13, cx: 144, cy: 80, w: 18, h: 18, angle: 15 },
  { id: 'UL6', num: 14, cx: 148, cy: 105, w: 24, h: 22, angle: 25 },
  { id: 'UL7', num: 15, cx: 155, cy: 130, w: 22, h: 20, angle: 35 },
  { id: 'UL8', num: 16, cx: 165, cy: 155, w: 22, h: 20, angle: 45 },
];

const DentalArchDiagram = ({
  title,
  arrows = [],
  highlightedTeeth = [],
  contactMarkers = [],
  label,
}) => {
  const [hoveredTooth, setHoveredTooth] = useState(null);

  return (
    <div className="flex flex-col items-center">
      {title && (
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
      )}
      <svg viewBox="0 0 210 190" className="w-full max-w-xs">
        {/* Arch outline */}
        <path
          d="M 45 160 Q 40 100 72 42 Q 90 10 105 8 Q 120 10 138 42 Q 170 100 165 160"
          fill="none"
          stroke="#d1d5db"
          strokeWidth="1"
          strokeDasharray="4 3"
        />

        {/* Midline */}
        <line x1="105" y1="5" x2="105" y2="170" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="3 3" />

        {/* Teeth */}
        {UPPER_TEETH.map((tooth) => {
          const isHighlighted = highlightedTeeth.includes(tooth.id);
          const isHovered = hoveredTooth === tooth.id;

          return (
            <g
              key={tooth.id}
              transform={`rotate(${tooth.angle}, ${tooth.cx}, ${tooth.cy})`}
              onMouseEnter={() => setHoveredTooth(tooth.id)}
              onMouseLeave={() => setHoveredTooth(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={tooth.cx - tooth.w / 2}
                y={tooth.cy - tooth.h / 2}
                width={tooth.w}
                height={tooth.h}
                rx={4}
                ry={4}
                fill={isHighlighted ? '#dbeafe' : isHovered ? '#f3f4f6' : 'white'}
                stroke={isHighlighted ? '#3b82f6' : isHovered ? '#9ca3af' : '#d1d5db'}
                strokeWidth={isHighlighted ? 1.5 : 1}
              />
              <text
                x={tooth.cx}
                y={tooth.cy + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="6"
                fill={isHighlighted ? '#1d4ed8' : '#6b7280'}
                fontWeight={isHighlighted ? 600 : 400}
                fontFamily="Inter, sans-serif"
              >
                {tooth.num}
              </text>
            </g>
          );
        })}

        {/* Contact markers (dots between teeth) */}
        {contactMarkers.map((marker, i) => (
          <circle
            key={`marker-${i}`}
            cx={marker.x}
            cy={marker.y}
            r={3}
            fill={marker.color || '#ef4444'}
            opacity={0.8}
          />
        ))}

        {/* Force arrows */}
        <defs>
          <marker
            id="arrowRed"
            markerWidth="6"
            markerHeight="4"
            refX="5"
            refY="2"
            orient="auto"
          >
            <polygon points="0 0, 6 2, 0 4" fill="#ef4444" />
          </marker>
          <marker
            id="arrowBlack"
            markerWidth="6"
            markerHeight="4"
            refX="5"
            refY="2"
            orient="auto"
          >
            <polygon points="0 0, 6 2, 0 4" fill="#1f2937" />
          </marker>
          <marker
            id="arrowBlue"
            markerWidth="6"
            markerHeight="4"
            refX="5"
            refY="2"
            orient="auto"
          >
            <polygon points="0 0, 6 2, 0 4" fill="#3b82f6" />
          </marker>
        </defs>
        {arrows.map((arrow, i) => (
          <line
            key={`arrow-${i}`}
            x1={arrow.x1}
            y1={arrow.y1}
            x2={arrow.x2}
            y2={arrow.y2}
            stroke={arrow.color || '#ef4444'}
            strokeWidth={1.5}
            markerEnd={`url(#arrow${arrow.color === '#1f2937' ? 'Black' : arrow.color === '#3b82f6' ? 'Blue' : 'Red'})`}
          />
        ))}

        {/* Label at bottom */}
        {label && (
          <text
            x="105"
            y="185"
            textAnchor="middle"
            fontSize="8"
            fill="#6b7280"
            fontFamily="Inter, sans-serif"
          >
            {label}
          </text>
        )}
      </svg>
    </div>
  );
};

export default DentalArchDiagram;
